import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { NoteItem, NoteVisibility } from '@crm/shared';
import { useAuth } from '../auth/AuthContext';
import { extractApiError } from '../../lib/api';
import {
  useAppointmentNotes,
  useNoteCreate,
  useNoteDelete,
  useNoteUpdate,
} from './notesApi';

type Props = {
  appointmentId: number;
};

export function NotesSection({ appointmentId }: Props): ReactNode {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  const { data: notes, isLoading, error } = useAppointmentNotes(appointmentId);
  const create = useNoteCreate(appointmentId);
  const update = useNoteUpdate(appointmentId);
  const remove = useNoteDelete(appointmentId);

  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState<NoteVisibility>('EMPLOYEE');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingBody, setEditingBody] = useState('');
  const [editingVisibility, setEditingVisibility] =
    useState<NoteVisibility>('EMPLOYEE');
  const [formError, setFormError] = useState<string | null>(null);

  async function handleAdd(): Promise<void> {
    setFormError(null);
    const trimmed = body.trim();
    if (trimmed.length === 0) {
      setFormError(t('notes.errors.bodyRequired'));
      return;
    }
    try {
      await create.mutateAsync({ body: trimmed, visibility });
      setBody('');
      setVisibility('EMPLOYEE');
    } catch (err) {
      setFormError(extractApiError(err));
    }
  }

  function startEdit(n: NoteItem): void {
    setEditingId(n.id);
    setEditingBody(n.body);
    setEditingVisibility(n.visibility);
    setFormError(null);
  }

  function cancelEdit(): void {
    setEditingId(null);
    setEditingBody('');
  }

  async function handleSaveEdit(id: number): Promise<void> {
    setFormError(null);
    const trimmed = editingBody.trim();
    if (trimmed.length === 0) {
      setFormError(t('notes.errors.bodyRequired'));
      return;
    }
    try {
      await update.mutateAsync({
        id,
        input: { body: trimmed, visibility: editingVisibility },
      });
      cancelEdit();
    } catch (err) {
      setFormError(extractApiError(err));
    }
  }

  async function handleDelete(id: number): Promise<void> {
    setFormError(null);
    if (!window.confirm(t('notes.deleteConfirm'))) return;
    try {
      await remove.mutateAsync(id);
    } catch (err) {
      setFormError(extractApiError(err));
    }
  }

  function canMutate(n: NoteItem): boolean {
    if (!user) return false;
    return isAdmin || n.authorId === user.id;
  }

  return (
    <section className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-700">
          {t('notes.title')}
        </h3>
        {notes && (
          <span className="text-xs text-zinc-500">
            {t('notes.count', { count: notes.length })}
          </span>
        )}
      </div>

      {isLoading && (
        <div className="text-sm text-zinc-500">{t('common.loading')}</div>
      )}
      {error && (
        <div className="rounded bg-red-50 p-2 text-sm text-red-700">
          {extractApiError(error)}
        </div>
      )}

      <ul className="mb-3 space-y-2">
        {notes?.length === 0 && (
          <li className="text-sm italic text-zinc-500">{t('notes.empty')}</li>
        )}
        {notes?.map((n) => (
          <li
            key={n.id}
            className="rounded-md border border-zinc-200 bg-white p-2 text-sm shadow-sm"
          >
            {editingId === n.id ? (
              <div className="space-y-2">
                <textarea
                  className="input min-h-[72px]"
                  value={editingBody}
                  onChange={(e) => setEditingBody(e.target.value)}
                  maxLength={5000}
                />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="flex items-center gap-2 text-xs text-zinc-600">
                    <input
                      type="checkbox"
                      checked={editingVisibility === 'TEAM'}
                      onChange={(e) =>
                        setEditingVisibility(e.target.checked ? 'TEAM' : 'EMPLOYEE')
                      }
                    />
                    {t('notes.shareWithTeam')}
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={cancelEdit}
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => void handleSaveEdit(n.id)}
                      disabled={update.isPending}
                    >
                      {update.isPending ? t('common.saving') : t('common.save')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                  <span className="font-medium text-zinc-700">
                    {n.author.firstName} {n.author.lastName}
                  </span>
                  <span>·</span>
                  <span>{new Date(n.createdAt).toLocaleString()}</span>
                  <VisibilityBadge visibility={n.visibility} />
                  {n.updatedAt !== n.createdAt && (
                    <span className="italic">({t('notes.edited')})</span>
                  )}
                </div>
                <p className="whitespace-pre-wrap text-zinc-800">{n.body}</p>
                {canMutate(n) && (
                  <div className="mt-2 flex gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => startEdit(n)}
                      className="text-brand-600 hover:underline"
                    >
                      {t('common.actions.edit')}
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(n.id)}
                      className="text-red-600 hover:underline"
                      disabled={remove.isPending}
                    >
                      {t('common.actions.delete')}
                    </button>
                  </div>
                )}
              </>
            )}
          </li>
        ))}
      </ul>

      {/* Formularz dodawania */}
      <div className="space-y-2 rounded-md border border-dashed border-zinc-300 bg-white p-2">
        <textarea
          className="input min-h-[72px]"
          placeholder={t('notes.placeholder') ?? ''}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={5000}
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-xs text-zinc-600">
            <input
              type="checkbox"
              checked={visibility === 'TEAM'}
              onChange={(e) =>
                setVisibility(e.target.checked ? 'TEAM' : 'EMPLOYEE')
              }
            />
            {t('notes.shareWithTeam')}
            <span className="text-zinc-400">
              ({visibility === 'TEAM'
                ? t('notes.visibility.TEAM')
                : t('notes.visibility.EMPLOYEE')})
            </span>
          </label>
          <button
            type="button"
            className="btn-primary"
            onClick={() => void handleAdd()}
            disabled={create.isPending}
          >
            {create.isPending ? t('common.saving') : t('notes.add')}
          </button>
        </div>
        {formError && (
          <div className="rounded bg-red-50 p-2 text-sm text-red-700">
            {formError}
          </div>
        )}
      </div>
    </section>
  );
}

function VisibilityBadge({
  visibility,
}: {
  visibility: NoteVisibility;
}): ReactNode {
  const { t } = useTranslation();
  const isTeam = visibility === 'TEAM';
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
        isTeam
          ? 'bg-emerald-100 text-emerald-700'
          : 'bg-zinc-200 text-zinc-700'
      }`}
    >
      {isTeam ? t('notes.visibility.TEAM') : t('notes.visibility.EMPLOYEE')}
    </span>
  );
}
